import { PropsWithChildren, memo } from 'react';

import { Space } from 'antd';

import { getCdnUrl } from '@/helpers';
import styled, { StyledProp, css, cx } from '@/styled';

import ImageRatio, { AspectRatioTypes } from '../ImageRatio';

type ImageProps = {
  aspect?: AspectRatioTypes;
  blur?: boolean;
} & Pick<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'onClick'>;

interface BaseCardProps extends StyledProp, React.HTMLAttributes<HTMLDivElement>, PropsWithChildren {
  image: ImageProps;
  extraContent?: React.ReactNode;
  internalSpace?: number;
}

const BaseCard = ({ children, image, extraContent, onClick, className, internalSpace = 8 }: BaseCardProps) => {
  const { blur = true, src = '', onClick: imageOnClick, ...rest } = image;
  const hasClick = typeof onClick === 'function';

  const currentSrcImage = getCdnUrl(src ?? '');

  return (
    <Space
      className={cx(className, 'base-card', { '--has-click': hasClick })}
      direction='vertical'
      size={internalSpace}
      onClick={onClick}
    >
      <div className={cx('base-card-image', imageOnClick && '--has-click')} onClick={imageOnClick}>
        <ImageRatio src={currentSrcImage} onClick={imageOnClick} {...rest} />
        {blur && <div className='base-card-imageBlur' style={{ backgroundImage: `url(${currentSrcImage})` }} />}
      </div>

      <Space className='base-card-data' direction='vertical' size={8}>
        {children}
      </Space>

      {extraContent && <div className='base-card-extraContent'>{extraContent}</div>}
    </Space>
  );
};

export default memo(styled(BaseCard)`
  ${({ theme }) => {
    return css`
      display: flex;
      position: relative;

      &.--has-click,
      .--has-click {
        cursor: pointer;
      }

      .base-card-image {
        border-radius: ${theme.borderRadius}px;
        overflow: hidden;
        position: relative;

        .base-card-imageBlur {
          position: absolute;
          top: -8px;
          left: -8px;
          width: calc(100% + 16px);
          height: calc(100% + 16px);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          filter: blur(5px);
          z-index: 1;
          border-radius: ${theme.borderRadius}px;
        }
      }

      .base-card-data {
        width: 100%;
        padding: ${theme.space.spacing(0, 2)};

        span.ant-typography {
          color: ${theme.colorTextSecondary};
          font-size: ${theme.space.pxToRem(16)};
        }

        span.ant-typography-secondary {
          color: ${theme.colorTextTertiary};
          font-size: ${theme.space.pxToRem(14)};
        }
      }
    `;
  }}
`);
